import React, { useEffect, useState } from "react";
import { Award, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

export const AdminCertificates = () => {
  const [certs, setCerts] = useState([]);
  const [traders, setTraders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", achievement_title: "Funded Trader", account_size: "10000" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/certificates").then((r) => setCerts(r.data || []));
    api.get("/admin/traders").then((r) => setTraders(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/certificates/issue", { ...form, account_size: parseFloat(form.account_size) });
      toast.success("Certificate issued and emailed");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-certificates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Certificates</h1>
          <p className="text-[#8a8a8a] text-sm mt-1">{certs.length} certificates issued</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="admin-issue-cert-button">
              <Plus size={16} className="mr-1.5" /> Issue Certificate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" data-testid="issue-cert-dialog">
            <DialogHeader><DialogTitle className="font-display">Issue Certificate</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Trader</Label>
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="cert-trader-select"><SelectValue placeholder="Select trader" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5] max-h-72">
                    {traders.map((t) => <SelectItem key={t.id} value={t.id} className="text-[#f5f5f5]">{t.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Achievement Title</Label>
                <Select value={form.achievement_title} onValueChange={(v) => setForm({ ...form, achievement_title: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                    <SelectItem value="Evaluation Passed" className="text-[#f5f5f5]">Evaluation Passed</SelectItem>
                    <SelectItem value="Funded Trader" className="text-[#f5f5f5]">Funded Trader</SelectItem>
                    <SelectItem value="Elite Performer" className="text-[#f5f5f5]">Elite Performer</SelectItem>
                    <SelectItem value="Master Trader" className="text-[#f5f5f5]">Master Trader</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Account Size (USD)</Label>
                <Input type="number" required value={form.account_size} onChange={(e) => setForm({ ...form, account_size: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="cert-account-size" />
              </div>
              <Button type="submit" disabled={busy || !form.user_id} className="w-full h-11 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="cert-issue-submit">
                {busy ? "Issuing..." : "Issue & Email PDF"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {certs.length === 0 && <div className="col-span-full glass rounded-xl p-10 text-center"><Award className="mx-auto text-[#ff7a00]" size={32} /><div className="mt-3 text-[#8a8a8a]">No certificates issued yet.</div></div>}
        {certs.map((c) => (
          <div key={c.id} className="glass rounded-xl p-5" data-testid={`admin-cert-${c.id}`}>
            <Award className="text-[#ff7a00]" size={24} />
            <div className="text-[10px] text-[#8a8a8a] font-mono mt-3">#{c.certificate_number}</div>
            <div className="font-display text-lg font-bold mt-1">{c.user_name}</div>
            <div className="text-[#ff9a2f] text-sm">{c.achievement_title}</div>
            <div className="font-display text-xl font-bold mt-2">${c.account_size.toLocaleString()}</div>
            <div className="text-xs text-[#8a8a8a] mt-2">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
